import { useEffect, useImperativeHandle, useState, forwardRef, useCallback, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { IKeyActivity } from '@/states/key-activities.state';
import { ITrain, ITrainSkillPayload } from '@/states/train.state';

export interface TrainMatrixRow {
  org_chart_node_id: number;
  org_chart_node_name: string;
  train?: ITrain;
}

export interface TrainMatrixTableRef {
  /** Collect the payloads for every row that has at least one non-blank skill. */
  collectPayloads: () => ITrainSkillPayload[];
}

interface TrainMatrixTableProps {
  rows: TrainMatrixRow[];
  tenantId: number | string;
  disabled?: boolean;
}

// Per-node Key Activities + the editable skill inputs keyed by KA id.
interface NodeState {
  keyActivities: IKeyActivity[];
  // org_key_activity_id -> list of skill name inputs
  skills: Record<number, string[]>;
  isLoading: boolean;
}

const KNOWLEDGE_ORG_FALLBACK = ['AOS#1-6', 'Doc. Job Desc', "Do vs Don't", 'Reward & Punishment'];

function buildSkillsFromTrain(keyActivities: IKeyActivity[], train?: ITrain): Record<number, string[]> {
  const map: Record<number, string[]> = {};
  keyActivities.forEach(ka => {
    const kaId = Number(ka.id);
    const existing = train?.skills?.find(s => s.org_key_activity_id === kaId);
    map[kaId] = existing && existing.name_of_skills.length > 0 ? [...existing.name_of_skills, ''] : [''];
  });
  return map;
}

// Build the skill payload entry for a single Key Activity (kept top-level to avoid deep nesting).
function buildKaSkill(ka: IKeyActivity, node: NodeState) {
  const names = (node.skills[Number(ka.id)] || []).map(s => s.trim()).filter(Boolean);
  return { org_key_activity_id: Number(ka.id), name_of_skills: names };
}

const TrainMatrixTable = forwardRef<TrainMatrixTableRef, TrainMatrixTableProps>(({ rows, tenantId, disabled }, ref) => {
  const rowsRef = useRef(rows);
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const [nodeStates, setNodeStates] = useState<Record<number, NodeState>>({});

  const fetchNode = useCallback(
    async (nodeId: number) => {
      setNodeStates(prev => ({
        ...prev,
        [nodeId]: { keyActivities: [], skills: {}, isLoading: true },
      }));
      try {
        const url = `${env.apiTasklistBaseUrl}/api/v1/compro/key-activity/${tenantId}`;
        const { data } = await axiosInstance.get(url, {
          params: { org_chart_node_id: nodeId, page_size: 9999 },
        });
        const activities: IKeyActivity[] = data.data?.content || data.data || [];
        setNodeStates(prev => {
          const latestRow = rowsRef.current.find(r => r.org_chart_node_id === nodeId);
          return {
            ...prev,
            [nodeId]: {
              keyActivities: activities,
              skills: buildSkillsFromTrain(activities, latestRow?.train),
              isLoading: false,
            },
          };
        });
      } catch {
        setNodeStates(prev => ({
          ...prev,
          [nodeId]: { keyActivities: [], skills: {}, isLoading: false },
        }));
      }
    },
    [tenantId],
  );

  // Fetch Key Activities for each visible row once.
  useEffect(() => {
    if (!tenantId) return;
    rows.forEach(row => {
      if (!nodeStates[row.org_chart_node_id]) fetchNode(row.org_chart_node_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tenantId]);

  // When the parent updates row.train (e.g. after the server responds with
  // saved data), re-sync the editable skills so existing values are not lost.
  // This is the fix for "data empty on restart": fetchNode runs before trains
  // are loaded from the server, so skills start empty. Once trains arrive the
  // rows prop is updated with the real train data, and we need to rebuild the
  // skills state from that data without re-fetching key activities.
  useEffect(() => {
    rows.forEach(row => {
      const existing = nodeStates[row.org_chart_node_id];
      if (!existing || existing.isLoading) return;
      // Only rebuild when train data has arrived and skills are still in the
      // "fresh empty" state (i.e. every array is [''] which means the user
      // hasn't typed anything yet), so we don't overwrite user edits.
      const allEmpty = Object.values(existing.skills).every(arr => arr.length === 1 && arr[0] === '');
      if (allEmpty && row.train) {
        setNodeStates(prev => ({
          ...prev,
          [row.org_chart_node_id]: {
            ...existing,
            skills: buildSkillsFromTrain(existing.keyActivities, row.train),
          },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const updateSkill = (nodeId: number, kaId: number, idx: number, value: string) => {
    setNodeStates(prev => {
      const node = prev[nodeId];
      if (!node) return prev;
      const arr = [...(node.skills[kaId] || [''])];
      arr[idx] = value;
      return { ...prev, [nodeId]: { ...node, skills: { ...node.skills, [kaId]: arr } } };
    });
  };

  const addSkill = (nodeId: number, kaId: number) => {
    setNodeStates(prev => {
      const node = prev[nodeId];
      if (!node) return prev;
      const arr = [...(node.skills[kaId] || ['']), ''];
      return { ...prev, [nodeId]: { ...node, skills: { ...node.skills, [kaId]: arr } } };
    });
  };

  const removeSkill = (nodeId: number, kaId: number, idx: number) => {
    setNodeStates(prev => {
      const node = prev[nodeId];
      if (!node) return prev;
      const arr = [...(node.skills[kaId] || [''])];
      arr.splice(idx, 1);
      return { ...prev, [nodeId]: { ...node, skills: { ...node.skills, [kaId]: arr.length > 0 ? arr : [''] } } };
    });
  };

  useImperativeHandle(ref, () => ({
    collectPayloads: () => {
      const payloads: ITrainSkillPayload[] = [];
      for (const row of rows) {
        const node = nodeStates[row.org_chart_node_id];
        if (!node) continue;
        const skills = node.keyActivities.map(ka => buildKaSkill(ka, node)).filter(s => s.name_of_skills.length > 0);
        if (skills.length > 0) {
          payloads.push({ org_chart_node_id: row.org_chart_node_id, skills });
        }
      }
      return payloads;
    },
  }));

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-x-auto bg-white dark:bg-gray-900">
      <table className="min-w-[650px] w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide w-40 sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">
              Jabatan
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide w-32">
              Culture
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">
              Skills
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide w-48">
              Knowledge Organization
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                Belum ada jabatan. Lengkapi struktur organisasi terlebih dahulu.
              </td>
            </tr>
          ) : (
            rows.map(row => {
              const node = nodeStates[row.org_chart_node_id];
              const cultureItems = (row.train?.culture_values ?? []).map(c => c.budaya);
              const knowledgeItems =
                row.train?.knowledge_organization && row.train.knowledge_organization.length > 0
                  ? row.train.knowledge_organization
                  : KNOWLEDGE_ORG_FALLBACK;
              return (
                <tr key={row.org_chart_node_id} className="border-b border-gray-200 dark:border-gray-700 align-top">
                  {/* Jabatan */}
                  <td className="px-4 py-4 font-bold text-gray-800 dark:text-white sticky left-0 bg-white dark:bg-gray-900 z-10">
                    {row.org_chart_node_name}
                  </td>

                  {/* Culture (read-only) */}
                  <td className="px-4 py-4">
                    <BulletList items={cultureItems} emptyLabel="—" />
                  </td>

                  {/* Skills (editable per Key Activity) */}
                  <td className="px-4 py-4">
                    <SkillsCell
                      node={node}
                      nodeId={row.org_chart_node_id}
                      disabled={disabled}
                      onUpdateSkill={updateSkill}
                      onAddSkill={addSkill}
                      onRemoveSkill={removeSkill}
                    />
                  </td>

                  {/* Knowledge Organization (read-only) */}
                  <td className="px-4 py-4">
                    <BulletList items={knowledgeItems} emptyLabel="—" />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
});

TrainMatrixTable.displayName = 'TrainMatrixTable';

interface SkillsCellProps {
  node?: NodeState;
  nodeId: number;
  disabled?: boolean;
  onUpdateSkill: (nodeId: number, kaId: number, idx: number, value: string) => void;
  onAddSkill: (nodeId: number, kaId: number) => void;
  onRemoveSkill: (nodeId: number, kaId: number, idx: number) => void;
}

function SkillsCell({ node, nodeId, disabled, onUpdateSkill, onAddSkill, onRemoveSkill }: Readonly<SkillsCellProps>) {
  if (node?.isLoading) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">Memuat aktivitas...</span>;
  }
  if (!node || node.keyActivities.length === 0) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">Belum ada Key Activity untuk jabatan ini.</span>;
  }
  return (
    <div className="flex flex-wrap gap-3">
      {node.keyActivities.map(ka => {
        const kaId = Number(ka.id);
        return (
          <KeyActivityCard
            key={kaId}
            ka={ka}
            kaId={kaId}
            skills={node.skills[kaId] || ['']}
            nodeId={nodeId}
            disabled={disabled}
            onUpdateSkill={onUpdateSkill}
            onAddSkill={onAddSkill}
            onRemoveSkill={onRemoveSkill}
          />
        );
      })}
    </div>
  );
}

interface KeyActivityCardProps extends Omit<SkillsCellProps, 'node'> {
  ka: IKeyActivity;
  kaId: number;
  skills: string[];
}

function KeyActivityCard({
  ka,
  kaId,
  skills,
  nodeId,
  disabled,
  onUpdateSkill,
  onAddSkill,
  onRemoveSkill,
}: Readonly<KeyActivityCardProps>) {
  return (
    <div className="min-w-[180px] flex-1 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-100">
        {ka.name || '-'}
      </div>
      <div className="p-2 space-y-2">
        {skills.map((skill, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <input
              type="text"
              disabled={disabled}
              value={skill}
              onChange={e => onUpdateSkill(nodeId, kaId, idx, e.target.value)}
              placeholder="Nama skill"
              className="flex-1 min-w-0 h-8 px-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400"
            />
            {!disabled && skills.length > 1 && (
              <button
                type="button"
                onClick={() => onRemoveSkill(nodeId, kaId, idx)}
                aria-label="Hapus skill"
                title="Hapus skill"
                className="shrink-0 flex items-center justify-center w-7 h-7 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
              >
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => onAddSkill(nodeId, kaId)}
            className="w-full flex items-center justify-center gap-1 h-7 text-xs text-blue-600 dark:text-blue-400 border border-dashed border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
          >
            <Plus size={12} /> Skill
          </button>
        )}
      </div>
    </div>
  );
}

function BulletList({ items, emptyLabel }: Readonly<{ items: string[]; emptyLabel: string }>) {
  if (!items || items.length === 0) return <span className="text-gray-400 dark:text-gray-500">{emptyLabel}</span>;
  return (
    <ul className="space-y-1">
      {items.map(item => (
        <li key={item} className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 shrink-0 inline-block" />
          <span className="text-sm text-gray-700 dark:text-gray-200">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default TrainMatrixTable;
